import "website_script/lib/events"
import auTracker, { trackFunction } from "website_script/lib/tracking"
import App from "../website_script/App.svelte"
import ModuleTypes from "../website_script/constants/ModuleTypes"
import { SCRIPT_ELEMENT_ID } from "./constants/ScriptElementId"
import type { OpenModalOpts } from "./types/common"

/**
 * Starts our website script Svelte application with all the needed configuration
 *
 * Also sets up the public API on the window object
 */
export const initialize = (window: Window) => {
  // If the App is already loaded, don't load it again
  if (window.auws?.app) return

  auTracker.init(
    window.auws.settings.enableAuEvents,
    window.auws.settings.auEventsContext
  )

  if (window.auws.settings.departmentAutoDetected) {
    // Track the auto-assignment of the department
    trackFunction(
      "Department automatically detected from URL",
      window.trackingConstants.eventTypeConstants.AUTOMATIC,
      window.trackingConstants.eventProductConstants.WEBSITE_SCRIPT
    )
  }

  // We cannot use DOMContentLoaded here because this can be executed
  // on the dealer website after the content is loaded
  // Ideally we need to find a better trigger to execute it
  const app: App = new App({
    target: document.body,
    props: {
      theme: window.auws.theme,
      texts: window.auws.texts,
      settings: window.auws.settings,
      config: window.AuEmbed.config,
    },
  })
  window.auws.app = app
  window.auws.ModuleTypes = ModuleTypes

  /**
   * Dispatches an event that opens a modal for the given module type with specified options
   * Makes the openModal available to be called anywhere from the dealer website, as part of our API
   */
  window.auws.openModal = (
    moduleType: ModuleTypes,
    opts: OpenModalOpts
  ): void => {
    const openModal = new CustomEvent("modalOpen", {
      detail: {
        moduleType: moduleType,
        opts: opts,
      },
    })
    window.dispatchEvent(openModal)
  }

  /**
   * API for the dealershops site to unload/destroy our app if needed
   */
  window.auws.unload = (): void => {
    if (window.auws.app?.$destroy) {
      window.auws.app.$destroy()
      delete window.auws.app
    }
  }

  window.AuEmbed.pushUrl = (url: string): void => {
    console.warn(
      "This function is deprecated, please switch to window.auws.load"
    )
    window.auws.load(url)
  }

  /**
   * API for loading up the widgets - automatically call unload
   */
  window.auws.load = (url: string): void => {
    window.auws.unload()
    initialize(window)
  }

  // Reloads the script, by recreating the script tag and sending the new url
  // TODO: once there is a way to fetch new config and re-initialize the app, this can be simplified
  window.auws.reload = () => {
    window.auws.unload()

    // Get the previous script and copy the URL
    const oldScript = document.getElementById(
      SCRIPT_ELEMENT_ID
    ) as HTMLScriptElement | null

    if (!oldScript) {
      console.warn("Cannot reload. The previous script is not found!")
      return
    }

    const url = new URL(oldScript.src)
    url.search = ""
    oldScript.id = SCRIPT_ELEMENT_ID + "-old"

    // Add the new script data - this allows for the new URL to be used
    const pageData = {
      depId: window.AuEmbed.config.departmentId,
      device: window.matchMedia("only screen and (max-width: 760px)").matches
        ? "mobile"
        : "desktop",
      pageType: window.AuEmbed.config.pageType,
      url: window.AuEmbed.config.previewUrl || window.location.href,
    }

    // Create the new script
    const script = document.createElement("script")
    script.async = true
    script.src =
      url.toString() + "?d=" + encodeURIComponent(JSON.stringify(pageData))
    script.id = SCRIPT_ELEMENT_ID
    const head = document.head
    head.insertBefore(script, head.lastChild)

    // Remove the previous script
    oldScript.remove()
  }

  // Support old API
  window.AuEmbed = window.AuEmbed || {}
  window.AuEmbed.TradeInCalculatorWidgetEmbedder = {
    openTradeInModal: () => window.auws.openModal(ModuleTypes.TRADE_IN),
  }
}
